<template>
  <div class="admin-wrap">
    <aside class="sidebar">
      <h2>Admin</h2>
      <nav>
        <button :class="{active: tab==='register'}" @click="tab='register'">Register User</button>
        <button :class="{active: tab==='roles'}" @click="tab='roles'">Assign Roles</button>
        <button :class="{active: tab==='users'}" @click="tab='users'">Users</button>
        <button :class="{active: tab==='rolePerms'}" @click="tab='rolePerms'">Role Permissions</button>
        <button :class="{active: tab==='rolesCrud'}" @click="tab='rolesCrud'">System Roles</button>

      </nav>
    </aside>

    <main class="content">
      <section v-if="tab==='register'">
        <h3>Register User</h3>
        <p class="hint">
          Only admins with <code>auth:user:create</code> can register users.
        </p>

        <form class="form" @submit.prevent="registerUser">
          <div class="row">
            <label>Requester Email (you)</label>
            <input v-model.trim="requesterEmail" type="email" placeholder="you@company.com" required />
          </div>
          <div class="row">
            <label>New User Email</label>
            <input v-model.trim="email" type="email" placeholder="new.user@company.com" required />
          </div>
          <div class="row">
            <label>Display Name</label>
            <input v-model.trim="display_name" type="text" placeholder="New User" required />
          </div>
          <div class="row">
            <label>Temporary Password</label>
            <input v-model="password" type="password" placeholder="Temp password" required />
          </div>

          <button :disabled="loading" type="submit">
            {{ loading ? 'Creating…' : 'Create User' }}
          </button>

          <p v-if="error" class="error">{{ error }}</p>
          <p v-if="success" class="success">
            ✅ User created. They’ll be asked to change password on first login.
          </p>
        </form>
      </section>

      <section v-else-if="tab==='roles'">
        <h3>Assign Roles / Rights</h3>
        <p class="hint">
          Only admins with <code>auth:role:grant</code> can modify roles.
        </p>

        <p v-if="rolesError" class="error">{{ rolesError }}</p>
        <p v-if="loadingRoles" class="muted">Loading users and roles…</p>

        <div v-if="!loadingRoles">
          <div class="row">
            <label>Select User</label>
            <select v-model.number="selectedUserId" @change="loadUserRoles">
              <option disabled value="">-- choose user --</option>
              <option v-for="u in users" :key="u.user_id" :value="u.user_id">
                {{ u.display_name }} ({{ u.email }})
              </option>
            </select>
          </div>

          <div v-if="selectedUserId">
            <h4>Roles for {{ currentUserLabel }}</h4>

            <div class="roles-grid">
              <label
                v-for="role in roles"
                :key="role.role_id"
                class="role-item"
              >
                <input
                  type="checkbox"
                  :value="role.role_id"
                  v-model="selectedRoleIds"
                />
                {{ role.role_name }}
              </label>
            </div>

            <button :disabled="savingRoles" @click="saveUserRoles">
              {{ savingRoles ? 'Saving…' : 'Save Roles' }}
            </button>

            <p v-if="rolesSuccess" class="success">✅ Roles updated.</p>
          </div>
        </div>
      </section>

      <section v-else-if="tab==='rolePerms'">
        <h3>Role Permissions</h3>
        <p class="hint">
          Only admins with <code>auth:role:grant</code> can modify role permissions.
        </p>

        <p v-if="permsError" class="error">{{ permsError }}</p>
        <p v-if="loadingPerms" class="muted">Loading roles and permissions…</p>

        <div v-if="!loadingPerms">
          <div class="row">
            <label>Select Role</label>
            <select v-model.number="selectedRoleId" @change="loadRolePermissions">
              <option disabled value="">-- choose role --</option>
              <option v-for="r in roles" :key="r.role_id" :value="r.role_id">
                {{ r.role_name }}
              </option>
            </select>
          </div>

          <div v-if="selectedRoleId">
            <h4>Permissions</h4>

            <div class="roles-grid">
              <label v-for="p in permissions" :key="p.perm_id" class="role-item">
                <input
                  type="checkbox"
                  :value="p.perm_id"
                  v-model="selectedPermIds"
                />
                {{ p.perm_key }}
              </label>
            </div>

            <button :disabled="savingPerms" @click="saveRolePermissions">
              {{ savingPerms ? 'Saving…' : 'Save Permissions' }}
            </button>

            <p v-if="permsSuccess" class="success">✅ Permissions updated.</p>
          </div>
        </div>
      </section>

      <section v-else-if="tab==='rolesCrud'">
        <h3>Roles (CRUD)</h3>
        <p class="hint">
          Create, rename, or delete roles.
        </p>

        <p v-if="crudError" class="error">{{ crudError }}</p>
        <p v-if="crudSuccess" class="success">{{ crudSuccess }}</p>

        <div class="row">
          <label>Create New Role</label>
          <input v-model.trim="newRoleName" placeholder="new-role" />
          <button :disabled="savingRole" @click="createRole">
            {{ savingRole ? 'Creating…' : 'Create' }}
          </button>
        </div>

        <hr />

        <div class="row">
          <label>Select Role</label>
          <select v-model.number="selectedRoleId">
            <option disabled value="">-- choose role --</option>
            <option v-for="r in roles" :key="r.role_id" :value="r.role_id">
              {{ r.role_name }}
            </option>
          </select>
        </div>

        <div v-if="selectedRoleId">
          <div class="row">
            <label>Rename Role</label>
            <input v-model.trim="renamedRoleName" placeholder="new-name" />
            <button :disabled="savingRole" @click="renameRole">
              {{ savingRole ? 'Saving…' : 'Rename' }}
            </button>
          </div>

          <div class="row">
            <label>Delete Role</label>
            <button class="danger" :disabled="deletingRole" @click="deleteRole">
              {{ deletingRole ? 'Deleting…' : 'Delete Role' }}
            </button>
          </div>
        </div>
      </section>






      <section v-else-if="tab==='users'">
        <h3>Users</h3>
        <p class="muted">
          Read-only list of users and their roles. (Activate/deactivate, token revocation can come later.)
        </p>

        <p v-if="rolesError" class="error">{{ rolesError }}</p>
        <p v-if="loadingUsers" class="muted">Loading users…</p>

        <button class="refresh-btn" @click="loadUsersList">
          Refresh
        </button>

        <table v-if="users.length" class="users-table">
          <thead>
            <tr>
              <th>Display Name</th>
              <th>Email</th>
              <th>Active</th>
              <th>Roles</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="u in users" :key="u.user_id">
              <td>{{ u.display_name }}</td>
              <td>{{ u.email }}</td>
              <td>{{ u.is_active ? 'Yes' : 'No' }}</td>
              <td>{{ userRolesById[u.user_id] || '—' }}</td>
            </tr>
          </tbody>
        </table>

        <p v-else-if="!loadingUsers" class="muted">
          No users found.
        </p>
      </section>

    </main>
  </div>
</template>

<script>
import axios from 'axios';



export default {
  name: 'AdminPage',
  data() {
    return {
      tab: 'register',
      requesterEmail: '',
      email: '',
      display_name: '',
      password: '',
      loading: false,
      error: '',
      success: false,

      // NEW for roles UI
      users: [],
      roles: [],
      loadingRoles: false,
      rolesError: '',
      rolesSuccess: false,
      selectedUserId: '',
      selectedRoleIds: [],
      savingRoles: false,

      // users tab
      userRolesById: {},   // { [user_id]: 'Role A, Role B' }
      loadingUsers: false,

      // permissions + role-permissions UI
      permissions: [],
      loadingPerms: false,
      permsError: '',
      permsSuccess: false,

      selectedRoleId: '',
      selectedPermIds: [],
      savingPerms: false,

      // roles CRUD
      newRoleName: '',
      renamedRoleName: '',
      crudError: '',
      crudSuccess: '',
      savingRole: false,
      deletingRole: false,

    };
    
  },

  computed: {
    currentUserLabel() {
      const u = this.users.find(u => u.user_id === this.selectedUserId);
      return u ? `${u.display_name} (${u.email})` : '';
    },
  },

  watch: {
    tab(newVal) {
      if (newVal === 'roles' && !this.users.length && !this.roles.length) {
        this.loadUsersAndRoles();
      }

      if (newVal === 'users' && !this.users.length) {
      this.loadUsersList();
      }

      if (newVal === 'rolePerms') {
        this.loadRolesAndPermissions();
      }

      if (newVal === 'rolesCrud' && !this.roles.length) {
        this.loadRolesOnly();
      }

    },
  },

  methods: {
    async registerUser() {
      this.error = '';
      this.success = false;
      if (!this.requesterEmail || !this.email || !this.display_name || !this.password) {
        this.error = 'All fields are required.';
        return;
      }
      this.loading = true;
      try {
        const payload = {
          requesterEmail: this.requesterEmail,
          email: this.email,
          display_name: this.display_name,
          password: this.password,
        };
        await axios.post('/auth/register', payload); // same-origin → Nginx proxy → Node
        this.success = true;
        this.email = '';
        this.display_name = '';
        this.password = '';
      } catch (e) {
        const msg = e?.response?.data?.error || e.message || 'Request failed.';
        this.error = String(msg);
      } finally {
        this.loading = false;
      }
    },

      // 👇 NEW: load all users & roles when entering Roles tab
    // Load all users & roles when entering Roles tab
    async loadUsersAndRoles() {
      this.rolesError = '';
      this.rolesSuccess = false;
      this.loadingRoles = true;
      try {
        const [usersRes, rolesRes] = await Promise.all([
          axios.get('/auth/users'),
          axios.get('/auth/roles'),
        ]);
        this.users = usersRes.data || [];
        this.roles = rolesRes.data || [];
      } catch (e) {
        const msg = e?.response?.data?.error || e.message || 'Failed to load users/roles.';
        this.rolesError = String(msg);
      } finally {
        this.loadingRoles = false;
      }
    },

    async loadUserRoles() {
      if (!this.selectedUserId) return;
      this.rolesError = '';
      this.rolesSuccess = false;
      this.selectedRoleIds = [];
      try {
        const { data } = await axios.get(`/auth/users/${this.selectedUserId}/roles`);

        // Support shapes: { roles: [...] }, { role_ids: [...] }, or array
        if (Array.isArray(data)) {
          this.selectedRoleIds = data.map(r => r.role_id);
        } else if (Array.isArray(data.role_ids)) {
          this.selectedRoleIds = data.role_ids;
        } else if (Array.isArray(data.roles)) {
          this.selectedRoleIds = data.roles.map(r => r.role_id);
        }
      } catch (e) {
        const msg = e?.response?.data?.error || e.message || 'Failed to load user roles.';
        this.rolesError = String(msg);
      }
    },

    async saveUserRoles() {
      if (!this.selectedUserId) return;
      this.rolesError = '';
      this.rolesSuccess = false;
      this.savingRoles = true;
      try {
        await axios.put(`/auth/users/${this.selectedUserId}/roles`, {
          role_ids: this.selectedRoleIds,
        });
        this.rolesSuccess = true;
      } catch (e) {
        const msg = e?.response?.data?.error || e.message || 'Failed to update roles.';
        this.rolesError = String(msg);
      } finally {
        this.savingRoles = false;
      }
    },

      async loadUsersList() {
      this.rolesError = '';
      this.loadingUsers = true;
      try {
        const { data } = await axios.get('/auth/users');
        this.users = data || [];

        // Optionally prefetch roles for each user so the table shows them
        await this.prefetchUserRoles();
      } catch (e) {
        const msg = e?.response?.data?.error || e.message || 'Failed to load users.';
        this.rolesError = String(msg);
      } finally {
        this.loadingUsers = false;
      }
    },

    async prefetchUserRoles() {
      const map = {};
      for (const u of this.users) {
        try {
          const { data } = await axios.get(`/auth/users/${u.user_id}/roles`);
          const rolesArr = Array.isArray(data.roles)
            ? data.roles
            : Array.isArray(data)
            ? data
            : [];
          map[u.user_id] = rolesArr.map(r => r.role_name).join(', ');
        } catch (e) {
          map[u.user_id] = '(error)';
        }
      }
      this.userRolesById = map;
    },

    async loadRolesAndPermissions() {
      this.permsError = '';
      this.permsSuccess = false;
      this.loadingPerms = true;

      try {
        const [rolesRes, permsRes] = await Promise.all([
          axios.get('/auth/roles'),
          axios.get('/auth/permissions'),
        ]);

        this.roles = rolesRes.data || [];
        this.permissions = permsRes.data || [];

      } catch (e) {
        this.permsError = e?.response?.data?.error || e.message || 'Failed to load roles/permissions.';
      } finally {
        this.loadingPerms = false;
      }
    },

    async loadRolePermissions() {
      if (!this.selectedRoleId) return;
      this.permsError = '';
      this.permsSuccess = false;
      this.selectedPermIds = [];

      try {
        const { data } = await axios.get(`/auth/roles/${this.selectedRoleId}/permissions`);

        const arr = Array.isArray(data.permissions)
          ? data.permissions
          : Array.isArray(data)
          ? data
          : [];

        this.selectedPermIds = arr.map(p => p.perm_id);

      } catch (e) {
        this.permsError = e?.response?.data?.error || e.message || 'Failed to load role permissions.';
      }
    },

    async saveRolePermissions() {
      if (!this.selectedRoleId) return;
      this.permsError = '';
      this.permsSuccess = false;
      this.savingPerms = true;

      try {
        await axios.put(`/auth/roles/${this.selectedRoleId}/permissions`, {
          perm_ids: this.selectedPermIds,
        });

        this.permsSuccess = true;

      } catch (e) {
        this.permsError = e?.response?.data?.error || e.message || 'Failed to save role permissions.';
      } finally {
        this.savingPerms = false;
      }
    },

    async loadRolesOnly() {
      this.crudError = '';
      try {
        const { data } = await axios.get('/auth/roles');
        this.roles = data || [];
      } catch (e) {
        this.crudError = e?.response?.data?.error || e.message || 'Failed to load roles.';
      }
    },

    async createRole() {
      if (!this.newRoleName.trim()) return;
      this.crudError = '';
      this.crudSuccess = '';
      this.savingRole = true;

      try {
        await axios.post('/auth/roles', { role_name: this.newRoleName.trim() });
        this.crudSuccess = '✅ Role created.';
        this.newRoleName = '';
        await this.loadRolesOnly();
      } catch (e) {
        this.crudError = e?.response?.data?.error || e.message || 'Failed to create role.';
      } finally {
        this.savingRole = false;
      }
    },

    async renameRole() {
      if (!this.selectedRoleId || !this.renamedRoleName.trim()) return;
      this.crudError = '';
      this.crudSuccess = '';
      this.savingRole = true;

      try {
        await axios.put(`/auth/roles/${this.selectedRoleId}`, {
          role_name: this.renamedRoleName.trim(),
        });

        this.crudSuccess = '✅ Role renamed.';
        await this.loadRolesOnly();
      } catch (e) {
        this.crudError = e?.response?.data?.error || e.message || 'Failed to rename role.';
      } finally {
        this.savingRole = false;
      }
    },

    async deleteRole() {
      if (!this.selectedRoleId) return;

      const role = this.roles.find(r => r.role_id === this.selectedRoleId);
      if (!role) return;

      if (!confirm(`Delete role "${role.role_name}"? This will remove it from all users.`)) return;

      this.crudError = '';
      this.crudSuccess = '';
      this.deletingRole = true;

      try {
        await axios.delete(`/auth/roles/${this.selectedRoleId}`);
        this.crudSuccess = '✅ Role deleted.';
        this.selectedRoleId = '';
        await this.loadRolesOnly();
      } catch (e) {
        this.crudError = e?.response?.data?.error || e.message || 'Failed to delete role.';
      } finally {
        this.deletingRole = false;
      }
    },









  },
};
</script>

<style scoped>
/* =========================
   Admin Page: Pablo Skin
   ========================= */

.admin-wrap {
  display: flex;
  min-height: calc(100vh - 80px);
  width: 100%;
  padding-top: 150px; /* keeps it below App.vue header if header is fixed */
  color: #d6caca;
  font-family: Garamond, serif;

  background:
    radial-gradient(1200px 700px at 50% 10%, rgba(85,149,183,0.14), transparent 60%),
    radial-gradient(900px 500px at 15% 85%, rgba(122,255,226,0.10), transparent 55%),
    #0b0101;
}

/* Sidebar = "machine-row" vibe but darker */
.sidebar {
  width: 260px;
  padding: 18px;
  border-right: 1px solid rgba(214,202,202,0.18);

  background: rgba(85,149,183,0.18);
  box-shadow:
    inset -1px 0 0 rgba(255,255,255,0.03),
    0 10px 22px rgba(0,0,0,0.35);

  border-top-right-radius: 14px;
  border-bottom-right-radius: 14px;
  backdrop-filter: blur(2px);
}

.sidebar h2 {
  margin: 4px 0 14px;
  font-family: 'Cormorant Garamond', Garamond, serif;
  font-size: 28px;
  letter-spacing: 1px;
  color: #f5f6f8;
  text-shadow: 0 2px 10px rgba(0,0,0,0.55);
}

.sidebar nav {
  display: grid;
  gap: 10px;
}

/* Sidebar buttons = option pills / machine-row hover */
.sidebar button {
  text-align: left;
  padding: 10px 12px;
  border: 1px solid rgba(214,202,202,0.22);
  border-radius: 12px;

  background: rgba(85,149,183,0.38);
  color: #f5f6f8;

  cursor: pointer;
  transition: transform 0.12s ease, background-color 0.12s ease, border-color 0.12s ease, box-shadow 0.12s ease;
  box-shadow:
    0 6px 14px rgba(0,0,0,0.22),
    inset 0 1px 0 rgba(255,255,255,0.05);
}

.sidebar button:hover {
  transform: translateY(-1px);
  background: rgba(85,149,183,0.52);
  border-color: rgba(214,202,202,0.34);
  box-shadow: 0 10px 22px rgba(0,0,0,0.30);
}

.sidebar button.active {
  background: rgba(122,255,226,0.22);
  border-color: rgba(122,255,226,0.55);
  color: #f5f6f8;
}

/* Content area = dark shell + card sections */
.content {
  flex: 1;
  padding: 24px;
}

.hint {
  color: rgba(214,202,202,0.75);
  margin: 0 0 16px;
}

/* Form panel = like an expanded row-body */
.form {
  display: grid;
  gap: 12px;
  max-width: 560px;

  padding: 16px 16px 18px;
  border-radius: 14px;

  background: rgba(122,255,226,0.10);
  border: 1px solid rgba(214,202,202,0.18);
  box-shadow:
    0 10px 22px rgba(0,0,0,0.30),
    inset 0 1px 0 rgba(255,255,255,0.04);
  backdrop-filter: blur(2px);
}

.row {
  display: grid;
  gap: 6px;
}

/* Inputs: dark-friendly */
input {
  padding: 10px 12px;
  border: 1px solid rgba(214,202,202,0.22);
  border-radius: 12px;
  font-size: 14px;

  background: rgba(11,1,1,0.55);
  color: #f5f6f8;
  box-shadow: inset 0 1px 0 rgba(255,255,255,0.03);
}

input::placeholder {
  color: rgba(214,202,202,0.55);
}

input:focus {
  outline: none;
  border-color: rgba(122,255,226,0.65);
  box-shadow: 0 0 0 2px rgba(122,255,226,0.18);
}

/* Submit button = Pablo teal */
button[type="submit"] {
  width: max-content;
  padding: 10px 14px;
  border: 1px solid rgba(214,202,202,0.18);
  border-radius: 12px;

  background: rgba(85,149,183,0.62);
  color: #0b0101;

  cursor: pointer;
  font-weight: 700;
  box-shadow: 0 10px 22px rgba(0,0,0,0.30);
  transition: transform 0.12s ease, background-color 0.12s ease, box-shadow 0.12s ease;
}

button[type="submit"]:hover {
  transform: translateY(-1px);
  background: rgba(85,149,183,0.74);
  box-shadow: 0 14px 28px rgba(0,0,0,0.35);
}

button[disabled] {
  opacity: 0.55;
  cursor: not-allowed;
}

/* Status text */
.error {
  color: #ff6b6b;
}

.success {
  color: rgba(122,255,226,0.95);
}

.muted {
  color: rgba(214,202,202,0.65);
}

/* Roles grid = option chips */
.roles-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: 8px;
  margin: 12px 0 16px;
}

.role-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 10px;
  border-radius: 12px;

  background: rgba(122,255,226,0.22);
  border: 1px solid rgba(0,0,0,0.18);
  color: #fff;

  font-size: 14px;
  font-weight: 700;
  box-shadow: 0 6px 14px rgba(0,0,0,0.22);
}

/* Users table = cleaner Pablo-ish */
.users-table {
  width: 100%;
  border-collapse: collapse;
  margin-top: 12px;
  font-size: 14px;

  border-radius: 14px;
  overflow: hidden;
  background: rgba(85,149,183,0.18);
  border: 1px solid rgba(214,202,202,0.18);
  box-shadow: 0 10px 22px rgba(0,0,0,0.30);
}

.users-table th,
.users-table td {
  padding: 10px 10px;
  text-align: left;
  border-bottom: 1px solid rgba(214,202,202,0.14);
}

.users-table th {
  color: #f5f6f8;
  font-family: 'Cormorant Garamond', Garamond, serif;
  letter-spacing: 1px;
  background: rgba(11,1,1,0.28);
}

.users-table tr:hover td {
  background: rgba(255,255,255,0.03);
}

/* Refresh button = subtle "pill" */
.refresh-btn {
  margin-top: 8px;
  margin-bottom: 8px;
  padding: 8px 12px;
  border-radius: 12px;

  border: 1px solid rgba(214,202,202,0.22);
  background: rgba(11,1,1,0.35);
  color: #f5f6f8;

  cursor: pointer;
  box-shadow: inset 0 1px 0 rgba(255,255,255,0.03);
  transition: transform 0.12s ease, background-color 0.12s ease;
}

.refresh-btn:hover {
  transform: translateY(-1px);
  background: rgba(255,255,255,0.06);
}

button.danger {
  background: #d9534f;
  color: white;
}
button.danger:hover {
  background: #c9302c;
}

</style>
